import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  getStaff,
  createStaff,
  updateStaff,
  deleteStaff,
  toggleStaffStatus,
  setFilters,
  resetFilters,
  clearError
} from "../../redux/slices/staffSlice";

// Material-UI Imports
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Card,
  CardContent,
  Avatar,
  Stack,
  Alert,
  Snackbar,
  Tooltip,
  Badge,
  Divider,
  InputAdornment,
  CircularProgress,
  Switch,
  FormControlLabel,
  Checkbox,
} from "@mui/material";

// Material-UI Icons
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Block as BlockIcon,
  CheckCircle as CheckCircleIcon,
  Search as SearchIcon,
  FilterList as FilterListIcon,
  Clear as ClearIcon,
  Close as CloseIcon,
  Person as PersonIcon,
  Phone as PhoneIcon,
  Lock as LockIcon,
  AdminPanelSettings as AdminIcon,
  Restaurant as ChefIcon,
  PersonOutline as WaiterIcon,
  SupervisorAccount as ManagerIcon,
  Refresh as RefreshIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Visibility as ViewIcon,
  MoreVert as MoreVertIcon,
  Download as DownloadIcon,
  WhatsApp as WhatsAppIcon,
  LocationOn as LocationIcon,
  Store as StoreIcon
} from "@mui/icons-material";

// Styled Components
import { styled } from '@mui/material/styles';

// Custom Styled Components
const StyledTableCell = styled(TableCell)(({ theme }) => ({
  fontWeight: 500,
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:nth-of-type(odd)': {
    backgroundColor: theme.palette.action.hover,
  },
  '&:hover': {
    backgroundColor: theme.palette.action.selected,
    cursor: 'pointer',
  },
}));

const RoleChip = styled(Chip)(({ theme, role }) => {
  const colors = {
    OWNER: { bg: '#FFE2E5', color: '#BF2600' },
    MANAGER: { bg: '#E3F2FD', color: '#1565C0' },
    CHEF: { bg: '#F3E5F5', color: '#7B1FA2' },
    WAITER: { bg: '#E8F5E8', color: '#2E7D32' },
    CASHIER: { bg: '#FFF3E0', color: '#ED6C02' },
    KITCHEN: { bg: '#FCE4EC', color: '#C2185B' },
    DELIVERY: { bg: '#E0F2F1', color: '#00695C' },
    STAFF: { bg: '#F5F5F5', color: '#616161' },
  };
  const defaultColor = { bg: '#F5F5F5', color: '#616161' };
  const { bg, color } = colors[role] || defaultColor;
  
  return {
    backgroundColor: bg,
    color: color,
    fontWeight: 600,
    '& .MuiChip-icon': {
      color: color,
    },
  };
});


const StatusBadge = styled(Badge)(({ theme, active }) => ({
  '& .MuiBadge-badge': {
    backgroundColor: active ? '#44b700' : '#ff1744',
    color: active ? '#44b700' : '#ff1744',
    boxShadow: `0 0 0 2px ${theme.palette.background.paper}`,
    '&::after': {
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      borderRadius: '50%',
      animation: active ? 'ripple 1.2s infinite ease-in-out' : 'none',
      border: '1px solid currentColor',
      content: '""',
    },
  },
  '@keyframes ripple': {
    '0%': {
      transform: 'scale(.8)',
      opacity: 1,
    },
    '100%': {
      transform: 'scale(2.4)',
      opacity: 0,
    },
  },
}));

const Staff = () => {
  const dispatch = useDispatch();
  const { staff, loading, error, pagination, filters } = useSelector((state) => state.staff);
  const { user } = useSelector((state) => state.user);
  
  // Local State
  const [showModal, setShowModal] = useState(false);
  const [editingStaff, setEditingStaff] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchText, setSearchText] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [selectedRole, setSelectedRole] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [viewMode, setViewMode] = useState('table');
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [showDetailsDialog, setShowDetailsDialog] = useState(false);
  const [formErrors, setFormErrors] = useState({});

  // Default permissions structure
  const defaultPermissions = {
    category: { create: false, read: true, update: false, delete: false },
    menu: { create: false, read: true, update: false, delete: false },
    order: { create: false, read: true, update: false, delete: false },
    offers: { create: false, read: false, update: false, delete: false },
    banner: { create: false, read: false, update: false, delete: false },
    staff: { create: false, read: false, update: false, delete: false },
    dashboard: { read: false },
    customer: { create: false, read: false, update: false, delete: false },
    reports: { read: false, export: false },
    settings: { update: false }
  };

  // Form State
  const [formData, setFormData] = useState({
    ownerName: "",
    mobile: "",
    password: "",
    role: "STAFF",
    whatsappNumber: "",
    restaurantAddress: "",
    isActive: true, // Default to true
    restaurantName: user?.restaurantName || "",
    permissions: { ...defaultPermissions }
  });

  // Role Icons Mapping
  const roleIcons = {
    OWNER: <AdminIcon />,
    MANAGER: <ManagerIcon />,
    CHEF: <ChefIcon />,
    WAITER: <WaiterIcon />,
    CASHIER: <PersonIcon />,
    KITCHEN: <ChefIcon />,
    DELIVERY: <PersonIcon />,
    STAFF: <PersonIcon />
  };

  useEffect(() => {
    dispatch(getStaff());
  }, [dispatch]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchText !== filters.search) {
        dispatch(setFilters({ search: searchText, page: 1 }));
        dispatch(getStaff({ search: searchText, page: 1 }));
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [searchText, dispatch, filters.search]);

  // Handle filter changes
  useEffect(() => {
    const filtersToApply = {};
    if (selectedRole) filtersToApply.role = selectedRole;
    if (selectedStatus) filtersToApply.isActive = selectedStatus === 'true';
    
    dispatch(setFilters({ ...filtersToApply, page: 1 }));
    dispatch(getStaff({ ...filtersToApply, page: 1 }));
  }, [selectedRole, selectedStatus, dispatch]);

  // Handle input change
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({ 
      ...prev, 
      [name]: type === 'checkbox' ? checked : value 
    }));
    
    // Clear error for this field
    if (formErrors[name]) {
      setFormErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  // Handle permission change
  const handlePermissionChange = (module, action) => {
    setFormData(prev => ({
      ...prev,
      permissions: {
        ...prev.permissions,
        [module]: {
          ...prev.permissions[module],
          [action]: !prev.permissions[module]?.[action]
        }
      }
    }));
  };

  // Validate form
  const validateForm = () => {
    const errors = {};
    if (!formData.ownerName?.trim()) {
      errors.ownerName = 'Name is required';
    }
    if (!formData.mobile?.trim()) {
      errors.mobile = 'Mobile number is required';
    } else if (!/^\d{10}$/.test(formData.mobile)) {
      errors.mobile = 'Invalid mobile number (10 digits required)';
    }
    if (!editingStaff && !formData.password) {
      errors.password = 'Password is required';
    } else if (formData.password && formData.password.length < 6) {
      errors.password = 'Password must be at least 6 characters';
    }
    return errors;
  };

  // Handle form submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    try {
      // Prepare data for submission
      const submitData = {
        ownerName: formData.ownerName,
        mobile: formData.mobile,
        role: formData.role,
        whatsappNumber: formData.whatsappNumber || formData.mobile,
        restaurantAddress: formData.restaurantAddress || "",
        isActive: formData.isActive,
        restaurantName: formData.restaurantName,
        permissions: formData.permissions
      };

      // Add password only if provided
      if (formData.password) {
        submitData.password = formData.password;
      }
      if (editingStaff) {
        await dispatch(updateStaff({ 
          id: editingStaff._id, 
          formData: submitData 
        })).unwrap();
        setSnackbar({ open: true, message: 'Staff updated successfully!', severity: 'success' });
      } else {
        await dispatch(createStaff(formData)).unwrap();
        setSnackbar({ open: true, message: 'Staff created successfully!', severity: 'success' });
      }
      
      setShowModal(false);
      resetForm();
      dispatch(getStaff()); // Refresh the list
      
    } catch (error) {
      console.error('Submit error:', error);
      setSnackbar({ 
        open: true, 
        message: typeof error === 'string' ? error : 'Something went wrong!', 
        severity: 'error' 
      });
    }
  };

  // Reset form
  const resetForm = () => {
    setFormData({
      ownerName: "",
      mobile: "",
      password: "",
      role: "STAFF",
      whatsappNumber: "",
      restaurantAddress: "",
      isActive: true,
      restaurantName: user?.restaurantName || "",
      permissions: { ...defaultPermissions }
    });
    setFormErrors({});
    setEditingStaff(null);
  };

  // Open edit modal
  const handleEdit = (staff) => {
    setEditingStaff(staff);
    setFormData({
      ownerName: staff.ownerName || "",
      mobile: staff.mobile || "",
      password: "",
      role: staff.role || "STAFF",
      whatsappNumber: staff.whatsappNumber || staff.mobile || "",
      restaurantAddress: staff.restaurantAddress || "",
      isActive: staff.isActive !== undefined ? staff.isActive : true,
      restaurantName: staff.restaurantName || user?.restaurantName || "",
      permissions: staff.permissions || { ...defaultPermissions }
    });
    setShowModal(true);
  };

  // Handle delete
  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this staff member?")) {
      try {
        await dispatch(deleteStaff(id)).unwrap();
        setSnackbar({ open: true, message: 'Staff deleted successfully!', severity: 'success' });
      } catch (error) {
        setSnackbar({ 
          open: true, 
          message: typeof error === 'string' ? error : 'Failed to delete!', 
          severity: 'error' 
        });
      }
    }
  };

  // Handle toggle status
  const handleToggleStatus = async (id) => {
    try {
      await dispatch(toggleStaffStatus(id)).unwrap();
      setSnackbar({ 
        open: true, 
        message: 'Status updated successfully!', 
        severity: 'success' 
      });
    } catch (error) {
      setSnackbar({ 
        open: true, 
        message: typeof error === 'string' ? error : 'Failed to update status!', 
        severity: 'error' 
      });
    }
  };

  // Handle page change
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    dispatch(getStaff({ page: newPage + 1, limit: rowsPerPage }));
  };

  // Handle rows per page change
  const handleChangeRowsPerPage = (event) => {
    const newLimit = parseInt(event.target.value, 10);
    setRowsPerPage(newLimit);
    setPage(0);
    dispatch(getStaff({ page: 1, limit: newLimit }));
  };

  // Handle view details
  const handleViewDetails = (staff) => {
    setSelectedStaff(staff);
    setShowDetailsDialog(true);
  };

  // Get role icon
  const getRoleIcon = (role) => {
    return roleIcons[role] || <PersonIcon />;
  };

  // Filter out OWNER role from display
  const filteredStaff = staff.filter(member => member.role !== 'OWNER');

  if (loading && staff.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
        <CircularProgress size={60} thickness={4} />
      </Box>
    );
  }

  return (
    <Box sx={{ mb:4, backgroundColor: '#f5f5f5', minHeight: '100vh' }}>
      {/* Header Section */}
      <Paper 
        elevation={3} 
        sx={{ 
          p: 3, 
          mb: 3, 
          borderRadius: 2,
          // background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
           bgcolor: "var(--bg-light)", 
          color: 'white'
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="h4" fontWeight={800} color="var(--text)">
              Staff Management
            </Typography>
            <Typography variant="body2" color="var(--text-light)">
              Manage your restaurant staff, roles, and permissions
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
               variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={() => dispatch(getStaff())}
             sx={{
                borderRadius: 2,
                textTransform: "none",
                fontWeight: 600,
                borderColor: "var(--border)",
                color: "var(--text)",
              }}
            >
              Refresh
            </Button>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => {
                resetForm();
                setShowModal(true);
              }}
                sx={{
                borderRadius: 2,
                textTransform: "none",
                fontWeight: 600,
                bgcolor: "var(--primary)",
                "&:hover": { bgcolor: "var(--primary-dark)" },
              }}
            >
              Add Staff
            </Button>
          </Box>
        </Box>

        {/* Stats Cards */}
        <Grid container spacing={3} sx={{ mt: 2 }}>
          <Grid size={{xs:12, sm:6, md:3}}>
            <Card sx={{ bgcolor: 'rgba(255,255,255,0.1)' }}>
              <CardContent>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>Total Staff</Typography>
                <Typography variant="h4" sx={{ fontWeight: 600 }}>{filteredStaff.length}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{xs:12, sm:6, md:3}}>
            <Card sx={{ bgcolor: 'rgba(255,255,255,0.1)' }}>
              <CardContent>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>Active Staff</Typography>
                <Typography variant="h4" sx={{ fontWeight: 600 }}>
                  {filteredStaff.filter(s => s.isActive).length}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{xs:12, sm:6, md:3}}>
            <Card sx={{ bgcolor: 'rgba(255,255,255,0.1)' }}>
              <CardContent>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>Managers</Typography>
                <Typography variant="h4" sx={{ fontWeight: 600 }}>
                  {filteredStaff.filter(s => s.role === 'MANAGER').length}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{xs:12, sm:6, md:3}}>
            <Card sx={{ bgcolor: 'rgba(255,255,255,0.1)' }}>
              <CardContent>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>Other Staff</Typography>
                <Typography variant="h4" sx={{ fontWeight: 600 }}>
                  {filteredStaff.filter(s => s.role !== 'MANAGER').length}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Paper>

      {/* Error Alert */}
      {error && (
        <Alert 
          severity="error" 
          sx={{ mb: 3 }}
          action={
            <IconButton color="inherit" size="small" onClick={() => dispatch(clearError())}>
              <CloseIcon fontSize="inherit" />
            </IconButton>
          }
        >
          {error}
        </Alert>
      )}

      {/* Search and Filter Bar */}
      <Paper elevation={2} sx={{ p: 2, mb: 3, borderRadius: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid size={{xs:12, md:4}}>
            <TextField
              fullWidth
              variant="outlined"
              placeholder="Search by name or mobile..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
                endAdornment: searchText && (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setSearchText('')}>
                      <ClearIcon />
                    </IconButton>
                  </InputAdornment>
                )
              }}
              size="small"
            />
          </Grid>
          
          <Grid size={{xs:12, md:3}}>
            <FormControl fullWidth size="small">
              <InputLabel>Role Filter</InputLabel>
              <Select
                value={selectedRole}
                onChange={(e) => setSelectedRole(e.target.value)}
                label="Role Filter"
              >
                <MenuItem value="">All Roles</MenuItem>
                <MenuItem value="MANAGER">Manager</MenuItem>
                <MenuItem value="CHEF">Chef</MenuItem>
                <MenuItem value="CASHIER">Cashier</MenuItem>
                <MenuItem value="KITCHEN">Kitchen</MenuItem>
                <MenuItem value="WAITER">Waiter</MenuItem>
                <MenuItem value="DELIVERY">Delivery</MenuItem>
                <MenuItem value="STAFF">Staff</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid size={{xs:12, md:3}}>
            <FormControl fullWidth size="small">
              <InputLabel>Status Filter</InputLabel>
              <Select
                value={selectedStatus}
                onChange={(e) => setSelectedStatus(e.target.value)}
                label="Status Filter"
              >
                <MenuItem value="">All Status</MenuItem>
                <MenuItem value="true">Active</MenuItem>
                <MenuItem value="false">Inactive</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid size={{xs:12, md:2}}>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Tooltip title="Toggle Filters">
                <IconButton 
                  onClick={() => setShowFilters(!showFilters)}
                  color={showFilters ? 'primary' : 'default'}
                >
                  <FilterListIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="Reset Filters">
                <IconButton onClick={() => {
                  setSelectedRole('');
                  setSelectedStatus('');
                  setSearchText('');
                  dispatch(resetFilters());
                  dispatch(getStaff());
                }}>
                  <ClearIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="View Mode">
                <IconButton onClick={() => setViewMode(viewMode === 'table' ? 'grid' : 'table')}>
                  {viewMode === 'table' ? '📊' : '📱'}
                </IconButton>
              </Tooltip>
            </Box>
          </Grid>
        </Grid>

        {/* Advanced Filters */}
        {showFilters && (
          <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid #e0e0e0' }}>
            <Grid container spacing={2}>
              <Grid size={{xs:12, md:3}}>
                <FormControl fullWidth size="small">
                  <InputLabel>Sort By</InputLabel>
                  <Select defaultValue="newest" label="Sort By">
                    <MenuItem value="newest">Newest First</MenuItem>
                    <MenuItem value="oldest">Oldest First</MenuItem>
                    <MenuItem value="name">Name</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid size={{xs:12, md:3}}>
                <FormControl fullWidth size="small">
                  <InputLabel>Date Range</InputLabel>
                  <Select defaultValue="all" label="Date Range">
                    <MenuItem value="all">All Time</MenuItem>
                    <MenuItem value="today">Today</MenuItem>
                    <MenuItem value="week">This Week</MenuItem>
                    <MenuItem value="month">This Month</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Box>
        )}
      </Paper>

      {/* Staff Display - Table View */}
      {viewMode === 'table' ? (
        <Paper elevation={3} sx={{ borderRadius: 2, overflow: 'hidden' }}>
          <TableContainer>
            <Table>
              <TableHead sx={{ bgcolor: '#f8f9fa' }}>
                <TableRow>
                  <StyledTableCell>Staff Member</StyledTableCell>
                  <StyledTableCell>Contact</StyledTableCell>
                  <StyledTableCell>Role</StyledTableCell>
                  <StyledTableCell>Status</StyledTableCell>
                  <StyledTableCell>Joined Date</StyledTableCell>
                  <StyledTableCell align="center">Actions</StyledTableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredStaff?.length > 0 ? (
                  filteredStaff?.map((member) => (
                    <StyledTableRow key={member._id}>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <StatusBadge
                            overlap="circular"
                            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                            variant="dot"
                            active={member.isActive ? 1 : 0}
                          >
                            <Avatar 
                            src={member.profileImage?.url}
                              sx={{ 
                                bgcolor: member.isActive ? '#4caf50' : '#f44336',
                                width: 40,
                                height: 40
                              }}
                            >
                              {member.ownerName?.charAt(0).toUpperCase() || '?'}
                            </Avatar>
                          </StatusBadge>
                          <Box sx={{ ml: 2 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                              {member.ownerName}
                            </Typography>
                            <Typography variant="caption" color="textSecondary">
                              ID: {member.employeeId || member._id?.slice(-6).toUpperCase()}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <PhoneIcon fontSize="small" color="action" />
                          <Typography variant="body2">{member.mobile}</Typography>
                        </Box>
                        {member.whatsappNumber && member.whatsappNumber !== member.mobile && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                            <WhatsAppIcon fontSize="small" color="success" />
                            <Typography variant="caption">{member.whatsappNumber}</Typography>
                          </Box>
                        )}
                      </TableCell>
                      <TableCell>
                        <RoleChip
                          role={member.role}
                          icon={getRoleIcon(member.role)}
                          label={member.role}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={member.isActive ? 'Active' : 'Inactive'}
                          size="small"
                          sx={{
                            bgcolor: member.isActive ? '#e8f5e8' : '#ffebee',
                            color: member.isActive ? '#2e7d32' : '#c62828',
                            fontWeight: 600
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {member.createdAt ? new Date(member.createdAt).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric'
                          }) : 'N/A'}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Stack direction="row" spacing={1} justifyContent="center">
                          <Tooltip title="View Details">
                            <IconButton 
                              size="small" 
                              color="info"
                              onClick={() => handleViewDetails(member)}
                            >
                              <ViewIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Edit">
                            <IconButton 
                              size="small" 
                              color="primary"
                              onClick={() => handleEdit(member)}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title={member.isActive ? 'Deactivate' : 'Activate'}>
                            <IconButton 
                              size="small" 
                              color={member.isActive ? 'warning' : 'success'}
                              onClick={() => handleToggleStatus(member._id)}
                            >
                              {member.isActive ? <BlockIcon fontSize="small" /> : <CheckCircleIcon fontSize="small" />}
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <IconButton 
                              size="small" 
                              color="error"
                              onClick={() => handleDelete(member._id)}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </TableCell>
                    </StyledTableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                      <Typography variant="body1" color="textSecondary">
                        No staff members found
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          
          {filteredStaff.length > 0 && (
            <TablePagination
              rowsPerPageOptions={[5, 10, 25, 50]}
              component="div"
              count={pagination.total || filteredStaff.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          )}
        </Paper>
      ) : (
        // Grid View
        <Grid container spacing={3}>
          {filteredStaff.map((member) => (
            <Grid size={{xs:12, sm:6, md:4, lg:3}} key={member._id}>
              <Card 
                elevation={3}
                sx={{
                  borderRadius: 2,
                  transition: 'transform 0.3s, box-shadow 0.3s',
                  '&:hover': {
                    transform: 'translateY(-5px)',
                    boxShadow: 6
                  }
                }}
              >
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                    <Badge
                      overlap="circular"
                      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                      variant="dot"
                      sx={{
                        '& .MuiBadge-badge': {
                          backgroundColor: member.isActive ? '#44b700' : '#ff1744',
                          color: member.isActive ? '#44b700' : '#ff1744',
                          boxShadow: `0 0 0 2px white`,
                        }
                      }}
                    >
                      <Avatar 
                        src={member.profileImage?.url}
                        sx={{ 
                          width: 60, 
                          height: 60,
                          bgcolor: member.isActive ? '#4caf50' : '#f44336',
                          fontSize: '1.5rem',
                          fontWeight: 'bold'
                        }}
                      >
                        {member.ownerName?.charAt(0).toUpperCase() || '?'}
                      </Avatar>
                    </Badge>
                    <RoleChip
                      role={member.role}
                      icon={getRoleIcon(member.role)}
                      label={member.role}
                      size="small"
                    />
                  </Box>

                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                    {member.ownerName}
                  </Typography>

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <PhoneIcon fontSize="small" color="action" />
                    <Typography variant="body2" color="textSecondary">
                      {member.mobile}
                    </Typography>
                  </Box>

                  {member.restaurantName && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <StoreIcon fontSize="small" color="action" />
                      <Typography variant="caption" color="textSecondary">
                        {member.restaurantName}
                      </Typography>
                    </Box>
                  )}

                  <Typography variant="caption" color="textSecondary" display="block" sx={{ mb: 2 }}>
                    Joined: {member.createdAt ? new Date(member.createdAt).toLocaleDateString() : 'N/A'}
                  </Typography>

                  <Divider sx={{ my: 2 }} />

                  <Box sx={{ display: 'flex', justifyContent: 'space-around' }}>
                    <Tooltip title="View">
                      <IconButton color="info" onClick={() => handleViewDetails(member)}>
                        <ViewIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Edit">
                      <IconButton color="primary" onClick={() => handleEdit(member)}>
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={member.isActive ? 'Deactivate' : 'Activate'}>
                      <IconButton 
                        color={member.isActive ? 'warning' : 'success'}
                        onClick={() => handleToggleStatus(member._id)}
                      >
                        {member.isActive ? <BlockIcon /> : <CheckCircleIcon />}
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton color="error" onClick={() => handleDelete(member._id)}>
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Create/Edit Modal */}
      <Dialog 
        open={showModal} 
        onClose={() => {
          setShowModal(false);
          resetForm();
        }}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 2 }
        }}
      >
        <DialogTitle sx={{ 
          // bgcolor: '#667eea',
            bgcolor: "var(--bg-light)", 
          // color: 'white',
          color:"var(--text)",
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {editingStaff ? <EditIcon /> : <AddIcon />}
            <Typography variant="h6">
              {editingStaff ? 'Edit Staff Member' : 'Add New Staff Member'}
            </Typography>
          </Box>
          <IconButton  color="var(--text)" onClick={() => {
            setShowModal(false);
            resetForm();
          }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <form onSubmit={handleSubmit}>
          <DialogContent dividers>
            <Grid container spacing={3}>
              {/* Basic Information */}
              <Grid size={{xs:12}}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                  Basic Information
                </Typography>
              </Grid>

              <Grid size={{xs:12, sm:6}}>
                <TextField
                  fullWidth
                  label="Full Name *"
                  name="ownerName"
                  value={formData.ownerName}
                  onChange={handleInputChange}
                  error={!!formErrors.ownerName}
                  helperText={formErrors.ownerName}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PersonIcon color="action" />
                      </InputAdornment>
                    )
                  }}
                />
              </Grid>

              <Grid size={{xs:12, sm:6}}>
                <TextField
                  fullWidth
                  label="Mobile Number *"
                  name="mobile"
                  value={formData.mobile}
                  onChange={handleInputChange}
                  error={!!formErrors.mobile}
                  helperText={formErrors.mobile}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PhoneIcon color="action" />
                      </InputAdornment>
                    )
                  }}
                />
              </Grid>

              <Grid size={{xs:12, sm:6}}>
                <TextField
                  fullWidth
                  label="WhatsApp Number"
                  name="whatsappNumber"
                  value={formData.whatsappNumber}
                  onChange={handleInputChange}
                  placeholder="Same as mobile if not specified"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <WhatsAppIcon color="success" />
                      </InputAdornment>
                    )
                  }}
                />
              </Grid>

              <Grid size={{xs:12, sm:6}}>
                <FormControl fullWidth required>
                  <InputLabel>Role</InputLabel>
                  <Select
                    name="role"
                    value={formData.role}
                    onChange={handleInputChange}
                    label="Role"
                  >
                    <MenuItem value="MANAGER">Manager</MenuItem>
                    <MenuItem value="CHEF">Chef</MenuItem>
                    <MenuItem value="CASHIER">Cashier</MenuItem>
                    <MenuItem value="KITCHEN">Kitchen</MenuItem>
                    <MenuItem value="WAITER">Waiter</MenuItem>
                    <MenuItem value="DELIVERY">Delivery</MenuItem>
                    <MenuItem value="STAFF">Staff</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid size={{xs:12}}>
                <TextField
                  fullWidth
                  label="Address"
                  name="restaurantAddress"
                  value={formData.restaurantAddress}
                  onChange={handleInputChange}
                  multiline
                  rows={2}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LocationIcon color="action" />
                      </InputAdornment>
                    )
                  }}
                />
              </Grid>

              <Grid size={{xs:12, sm:6}}>
                <TextField
                  fullWidth
                  label="Restaurant Name"
                  disabled={formData.restaurantName}
                  name="restaurantName"
                  value={formData.restaurantName}
                  onChange={handleInputChange}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <StoreIcon color="action" />
                      </InputAdornment>
                    )
                  }}
                />
              </Grid>

              <Grid size={{xs:12, sm:6}}>
                <TextField
                  fullWidth
                  type="password"
                  label={editingStaff ? "New Password (leave blank to keep current)" : "Password *"}
                  name="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  error={!!formErrors.password}
                  helperText={formErrors.password}
                  required={!editingStaff}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LockIcon color="action" />
                      </InputAdornment>
                    )
                  }}
                />
              </Grid>

              <Grid size={{xs:12, sm:6}}> 
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.isActive}
                      onChange={handleInputChange}
                      name="isActive"
                      color="primary"
                    />
                  }
                  label="Active Status"
                />
              </Grid>

              {/* Permissions Section */}
              <Grid size={{xs:12}}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, mt: 2 }}>
                  Permissions
                </Typography>
                <Paper variant="outlined" sx={{ p: 2, maxHeight: 400, overflow: 'auto' }}>
                  <TableContainer>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 600 }}>Module</TableCell>
                          <TableCell align="center" sx={{ fontWeight: 600 }}>Create</TableCell>
                          <TableCell align="center" sx={{ fontWeight: 600 }}>Read</TableCell>
                          <TableCell align="center" sx={{ fontWeight: 600 }}>Update</TableCell>
                          <TableCell align="center" sx={{ fontWeight: 600 }}>Delete</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {Object.keys(formData.permissions).map((module) => (
                          <TableRow key={module}>
                            <TableCell sx={{ textTransform: 'capitalize', fontWeight: 500 }}>
                              {module}
                            </TableCell>
                            <TableCell align="center">
                              {'create' in formData.permissions[module] && (
                                <Checkbox
                                  checked={formData.permissions[module]?.create || false}
                                  onChange={() => handlePermissionChange(module, "create")}
                                  color="primary"
                                  size="small"
                                />
                              )}
                            </TableCell>
                            <TableCell align="center">
                              {'read' in formData.permissions[module] && (
                                <Checkbox
                                  checked={formData.permissions[module]?.read || false}
                                  onChange={() => handlePermissionChange(module, "read")}
                                  color="primary"
                                  size="small"
                                />
                              )}
                            </TableCell>
                            <TableCell align="center">
                              {'update' in formData.permissions[module] && (
                                <Checkbox
                                  checked={formData.permissions[module]?.update || false}
                                  onChange={() => handlePermissionChange(module, "update")}
                                  color="primary"
                                  size="small"
                                />
                              )}
                            </TableCell>
                            <TableCell align="center">
                              {'delete' in formData.permissions[module] && (
                                <Checkbox
                                  checked={formData.permissions[module]?.delete || false}
                                  onChange={() => handlePermissionChange(module, "delete")}
                                  color="primary"
                                  size="small"
                                />
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Paper>
              </Grid>
            </Grid>
          </DialogContent>

          <DialogActions sx={{ p: 2 }}>
            <Button
              variant="outlined"
              startIcon={<CancelIcon />}
              onClick={() => {
                setShowModal(false);
                resetForm();
              }}
                sx={{
                borderRadius: 2,
                textTransform: "none",
                fontWeight: 600,
                borderColor: "var(--border)",
                color: "var(--text)",
              }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              startIcon={<SaveIcon />}
              disabled={loading}
               sx={{
                borderRadius: 2,
                textTransform: "none",
                fontWeight: 600,
                bgcolor: "var(--primary)",
                "&:hover": { bgcolor: "var(--primary-dark)" },
              }}
            >
              {loading ? <CircularProgress size={24} /> : (editingStaff ? 'Update' : 'Create')}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* View Details Dialog */}
      <Dialog
        open={showDetailsDialog}
        onClose={() => setShowDetailsDialog(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 2 }
        }}
      >
        {selectedStaff && (
          <>
            <DialogTitle sx={{ 
              bgcolor: '#f8f9fa',
              borderBottom: '1px solid #e0e0e0'
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar src={selectedStaff?.profileImage?.url} sx={{ bgcolor: "var(--primary)", width: 50, height: 50 }}>
                  {selectedStaff?.ownerName?.charAt(0).toUpperCase()}
                </Avatar>
                <Box>
                  <Typography variant="h6">{selectedStaff.ownerName}</Typography>
                  <Typography variant="caption" color="textSecondary">
                   Staff Details
                  </Typography>
                </Box>
              </Box>
            </DialogTitle>
            <DialogContent dividers>
              <Grid container spacing={2}>
                <Grid size={{xs:6}}>
                  <Typography variant="subtitle2" color="textSecondary">Mobile</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>{selectedStaff.mobile}</Typography>
                </Grid>
                <Grid size={{xs:6}}>
                  <Typography variant="subtitle2" color="textSecondary">WhatsApp</Typography>
                  <Typography variant="body1">{selectedStaff.whatsappNumber || selectedStaff.mobile}</Typography>
                </Grid>
                <Grid size={{xs:6}}>
                  <Typography variant="subtitle2" color="textSecondary">Role</Typography>
                  <Box sx={{ mt: 0.5 }}>
                    {/* <RoleChip role={selectedStaff?.role} />
                    {selectedStaff.role} */}

                     <RoleChip
                      role={selectedStaff.role}
                      icon={getRoleIcon(selectedStaff.role)}
                      label={selectedStaff.role}
                      size="small"
                    />
                  </Box>
                </Grid>
                <Grid size={{xs:6}}>
                  <Typography variant="subtitle2" color="textSecondary">Status</Typography>
                  <Box sx={{ mt: 0.5 }}>
                    <Chip
                      label={selectedStaff.isActive ? 'Active' : 'Inactive'}
                      size="small"
                      sx={{
                        bgcolor: selectedStaff.isActive ? '#e8f5e8' : '#ffebee',
                        color: selectedStaff.isActive ? '#2e7d32' : '#c62828'
                      }}
                    />
                  </Box>
                </Grid>
                <Grid size={{xs:6}}>
                  <Typography variant="subtitle2" color="textSecondary">Restaurant</Typography>
                  <Typography variant="body1">{selectedStaff.restaurantName || 'N/A'}</Typography>
                </Grid>
                <Grid size={{xs:6}}>
                  <Typography variant="subtitle2" color="textSecondary">Joined Date</Typography>
                  <Typography variant="body1">
                    {selectedStaff.createdAt ? new Date(selectedStaff.createdAt).toLocaleDateString() : 'N/A'}
                  </Typography>
                </Grid>
                {selectedStaff.restaurantAddress && (
                  <Grid size={{xs:12}}>
                    <Typography variant="subtitle2" color="textSecondary">Address</Typography>
                    <Typography variant="body1">{selectedStaff.restaurantAddress}</Typography>
                  </Grid>
                )}
                <Grid size={{xs:12}}>
                  <Typography variant="subtitle2" color="textSecondary" sx={{ mb: 1 }}>
                    Permissions
                  </Typography>
                  <Paper variant="outlined" sx={{ p: 2, maxHeight: 300, overflow: 'auto' }}>
                    {selectedStaff.permissions && Object.keys(selectedStaff.permissions).length > 0 ? (
                      Object.entries(selectedStaff.permissions).map(([module, perms]) => (
                        <Box key={module} sx={{ mb: 2 }}>
                          <Typography variant="subtitle2" sx={{ textTransform: 'capitalize', mb: 1 }}>
                            {module}:
                          </Typography>
                          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                            {Object.entries(perms).map(([action, allowed]) => (
                              allowed && (
                                <Chip
                                  key={action}
                                  label={action}
                                  size="small"
                                  sx={{ 
                                    bgcolor: '#e3f2fd', 
                                    color: '#1565c0',
                                    textTransform: 'capitalize'
                                  }}
                                />
                              )
                            ))}
                          </Box>
                        </Box>
                      ))
                    ) : (
                      <Typography variant="body2" color="textSecondary">
                        No specific permissions assigned
                      </Typography>
                    )}
                  </Paper>
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions sx={{ p: 2 }}>
              <Button onClick={() => setShowDetailsDialog(false)} variant="contained" sx={{
                borderRadius: 2,
                textTransform: "none",
                fontWeight: 600,
                bgcolor: "var(--primary)",
                "&:hover": { bgcolor: "var(--primary-dark)" },
              }}>
                Close
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Staff;